import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, MapPin, Calendar, Clock, Car, IndianRupee, Users, ArrowRight, Trash2, Edit } from 'lucide-react';
import RefreshButton from '../../components/ui/RefreshButton';
import { onewayTripAPI } from '../../services/api';
import { formatVehicleName } from '../../lib/utils';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/ui/ConfirmModal';
import Toast from '../../components/ui/Toast';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  booked: 'bg-blue-100 text-blue-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
  expired: 'bg-yellow-100 text-yellow-800',
};

export default function OneWayTripList() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // read-only monitor: paginated 12/page, no create/edit/delete
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0, pages: 0 });
  const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', type: 'danger', confirmText: 'Confirm', onConfirm: null });
  const [toastState, setToastState] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  const fetchTrips = async (page = 1) => {
    setLoading(true);
    try {
      // Admin gets paginated results (latest 12); owners get their full list.
      const response = await onewayTripAPI.getMy(isAdmin ? { page, limit: 12 } : undefined);
      let data = response.data?.data || [];
      if (filter !== 'all') {
        data = data.filter(t => t.status === filter);
      }
      setTrips(data);
      if (response.data?.pagination) setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load one-way trips');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrips(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleDelete = async (id) => {
    setConfirm({
      isOpen: true,
      title: 'Cancel Trip?',
      message: 'Are you sure you want to cancel this trip?',
      type: 'warning',
      confirmText: 'Yes, Cancel',
      onConfirm: async () => {
        try {
          await onewayTripAPI.delete(id);
          toast.success('Trip cancelled');
          fetchTrips();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to cancel trip');
        }
      },
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">One-Way Trips</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAdmin ? "Monitoring — all owners' one-way trips (read-only)" : 'List your vehicle for one-way return trips'}
          </p>
        </div>
        <div className="flex gap-3">
          <RefreshButton onClick={() => fetchTrips(pagination.page)} loading={loading} />
          {!isAdmin && (
            <Link
              to="/oneway-trips/create"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Trip
            </Link>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {['all', 'active', 'booked', 'completed', 'expired', 'cancelled'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize whitespace-nowrap transition-colors ${
              filter === status
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Trip Cards */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : trips.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
          <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No one-way trips</h3>
          <p className="text-sm text-gray-500 mb-6">
            {isAdmin ? 'No one-way trips found.' : 'List your vehicle for a one-way trip when it needs to return to base'}
          </p>
          {!isAdmin && (
            <Link
              to="/oneway-trips/create"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              Create Your First Trip
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {trips.map(trip => (
            <div key={trip._id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              {/* Route */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1">
                  <p className="text-sm text-gray-500">From</p>
                  <p className="font-semibold text-gray-900 capitalize">{trip.fromCity}</p>
                  {trip.fromState && <p className="text-xs text-gray-400 capitalize">{trip.fromState}</p>}
                </div>
                <ArrowRight className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div className="flex-1 text-right">
                  <p className="text-sm text-gray-500">To</p>
                  <p className="font-semibold text-gray-900 capitalize">{trip.toCity}</p>
                  {trip.toState && <p className="text-xs text-gray-400 capitalize">{trip.toState}</p>}
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(trip.departureDate)}</span>
                </div>
                {trip.departureTime && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{trip.departureTime}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600">
                  <IndianRupee className="w-4 h-4" />
                  <span className="font-semibold text-gray-900">₹{trip.price?.toLocaleString('en-IN')}</span><span className="text-xs">/person</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="w-4 h-4" />
                  <span>{trip.seatsAvailable} seats</span>
                </div>
              </div>

              {/* Vehicle */}
              {trip.carId && (
                <div className="flex items-center gap-3 mb-4 p-2 bg-gray-50 rounded-lg">
                  <div className="w-14 h-10 rounded-md overflow-hidden bg-gray-200 flex-shrink-0">
                    {trip.carId.images && trip.carId.images.length > 0 ? (
                      <img src={trip.carId.images[0]} alt={formatVehicleName(trip.carId, { showYear: false })} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Car className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{formatVehicleName(trip.carId)}</p>
                    <p className="text-xs text-gray-500">{trip.carId.fuelType} · {trip.carId.seats} seats</p>
                  </div>
                </div>
              )}

              {/* Status & Actions */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[trip.status] || 'bg-gray-100 text-gray-600'}`}>
                  {trip.status}
                </span>
                {!isAdmin && trip.status === 'active' && (
                  <div className="flex gap-2">
                    <Link
                      to={`/oneway-trips/edit/${trip._id}`}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => handleDelete(trip._id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {isAdmin && trip.carId?.ownerId?.travelsInfo?.name && (
                  <span className="text-[11px] text-gray-400">{trip.carId.ownerId.travelsInfo.name}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination — admin monitoring view */}
      {isAdmin && !loading && pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-6 bg-white rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-600">
          <span>Page {pagination.page} of {pagination.pages} · {pagination.total} total</span>
          <div className="flex items-center gap-2">
            <button onClick={() => fetchTrips(pagination.page - 1)} disabled={pagination.page <= 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <button onClick={() => fetchTrips(pagination.page + 1)} disabled={pagination.page >= pagination.pages}
              className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}

      <ConfirmModal isOpen={confirm.isOpen} onClose={() => setConfirm(p => ({ ...p, isOpen: false }))} onConfirm={confirm.onConfirm} title={confirm.title} message={confirm.message} type={confirm.type} confirmText={confirm.confirmText} />
      <Toast isOpen={toastState.isOpen} onClose={() => setToastState(p => ({ ...p, isOpen: false }))} title={toastState.title} message={toastState.message} type={toastState.type} />
    </div>
  );
}
